import { useCallback, useState } from 'react';
import { useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { useParams } from 'react-router';
import { Form, Input, Select, Alert, Button } from 'antd';
import type {
  ToolkitConfig,
  ToolkitConfigInput,
  ToolkitType,
} from '@viq-admin/types';
import { MaskedSecretField, errorMessageWithFieldErrors, space } from '@viq-admin/ui';
import { ATS_LAYOUT_EXTRA, ATS_LAYOUT_PLACEHOLDER, ATS_PASSWORD_EXTRA, ATS_PASSWORD_WHITESPACE_RULE } from './atsCopy';
import { FPLEADS_BASE_URL_EXTRA, FPLEADS_HTTPS_RULE, FPLEADS_SECRETS_EXTRA, FPLEADS_WHITESPACE_RULE } from './fpleadsCopy';
import { ATS_API_PASSWORD_RULE, ATS_API_SECRETS_EXTRA, ATS_API_SHOW_ID_EXTRA, ATS_API_SHOW_ID_RULE, ATS_API_USERNAME_RULE } from './atsApiCopy';
import { SHOWCARE_BASE_URL_EXTRA, SHOWCARE_HTTPS_RULE, SHOWCARE_LICENSE_KEY_EXTRA, SHOWCARE_LICENSE_KEY_RULE, SHOWCARE_BASE_URL_SHAPE_RULE } from './showcareCopy';
import { RequireCan } from '../../components/RequireCan';
import { Can } from '../../ability/AbilityContext';

/**
 * Edit a reusable toolkit config as a BARE form — no Refine `<Edit>` page
 * chrome — so it can render in place inside the shared `DetailPage`. Save
 * does NOT navigate (`redirect: false`); success is reported via `onSaved`
 * so `DetailPage` flips back to read mode. The form derives its own `dirty`
 * (useForm exposes none) and reports it via `onDirtyChange`.
 * `warnWhenUnsavedChanges: false` — `DetailPage` owns the discard guard.
 * Mirrors `CompanyEdit`/`IntegrationEdit`'s bare-form pattern; differences:
 *  - the `toolkitType` select is DISABLED — the type is fixed after create,
 *    and drives which of the 6 field groups below renders;
 *  - `toolkitType` is RESENT on save (immutable, but the API body still
 *    needs it) — it is never itself editable;
 *  - secret fields (`MaskedSecretField`) are optional (blank keeps the
 *    stored one) — the `required` rule applies only when there is no secret
 *    yet, each gated per-field to `manage Toolkit`, with an info Alert when
 *    a secret is already stored.
 * The Delete button now lives in `ToolkitDetail`'s read-mode header, not
 * here.
 *
 * All props are optional so the standalone edit test can render
 * `<ToolkitsEdit/>` bare. `id` is passed explicitly for robustness (at
 * /toolkits/:id the inferred Refine action is `show`, not `edit`); it falls
 * back to the route param.
 */

const TOOLKIT_OPTIONS: { label: string; value: ToolkitType }[] = [
  { label: 'attendee_list', value: 'attendee_list' },
  { label: 'plus_lead', value: 'plus_lead' },
  { label: 'expo_tools', value: 'expo_tools' },
  { label: 'badge_kit', value: 'badge_kit' },
  { label: 'conexsys (not implemented)', value: 'conexsys' },
  { label: 'xpress_lead_pro', value: 'xpress_lead_pro' },
  { label: 'maritz_swap', value: 'maritz_swap' },
  { label: 'edc', value: 'edc' },
  { label: 'exposmart', value: 'exposmart' },
  { label: 'ats', value: 'ats' },
  { label: 'fpleads', value: 'fpleads' },
  { label: 'showcare', value: 'showcare' },
  { label: 'ats_api', value: 'ats_api' },
];

interface FormShape {
  name: string;
  toolkitType?: ToolkitType;
  config?: Record<string, string>;
  secrets?: Record<string, string>;
}

export interface ToolkitsEditProps {
  id?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ToolkitsEdit({ id, onSaved, onDirtyChange }: ToolkitsEditProps = {}) {
  const params = useParams<{ id: string }>();
  const recordId = id ?? params.id;
  const [, setDirty] = useState(false);

  const { formProps, saveButtonProps, query, onFinish } = useForm<
    ToolkitConfig,
    HttpError,
    ToolkitConfigInput
  >({
    resource: 'toolkits',
    action: 'edit',
    id: recordId,
    redirect: false,
    warnWhenUnsavedChanges: false,
    errorNotification: (error) => ({
      key: 'toolkit-config-save-error',
      type: 'error',
      message: errorMessageWithFieldErrors(error, 'Failed to save toolkit config'),
    }),
    onMutationSuccess: () => {
      setDirtyOnce(false);
      onSaved?.();
    },
  });

  const record = query?.data?.data;
  const hasSecret = record?.hasSecret ?? false;
  const toolkitType = record?.toolkitType;

  const setDirtyOnce = useCallback(
    (next: boolean) => {
      setDirty((prev) => {
        if (prev !== next) onDirtyChange?.(next);
        return next;
      });
    },
    [onDirtyChange],
  );

  function handleFinish(values: FormShape) {
    const secretsFilled = Object.values(values.secrets ?? {}).filter(
      (v) => typeof v === 'string' && v.length > 0,
    ).length;
    const body: ToolkitConfigInput = {
      name: values.name,
      // toolkitType is immutable; resend the record's value to satisfy the
      // API body shape without letting the user change it.
      toolkitType: record?.toolkitType as ToolkitType,
      config: values.config ?? {},
    };
    if (secretsFilled > 0) {
      body.secrets = values.secrets;
    }
    return onFinish(body);
  }

  return (
    <RequireCan I="manage" a="Toolkit">
      <Form
        {...formProps}
        layout="vertical"
        initialValues={record}
        onValuesChange={() => setDirtyOnce(true)}
        onFinish={(values) => handleFinish(values as FormShape)}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input maxLength={200} />
        </Form.Item>

        <Form.Item label="Toolkit type" name="toolkitType">
          <Select options={TOOLKIT_OPTIONS} disabled />
        </Form.Item>

        {toolkitType === 'attendee_list' && (
          <>
            <Form.Item
              label="API URL"
              name={['config', 'url']}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input placeholder="https://api.example.com/attendee" />
            </Form.Item>
            <Form.Item
              label="Event UID"
              name={['config', 'evt_uid']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="Token"
                name={['secrets', 'token']}
                rules={
                  !hasSecret
                    ? [{ required: true, message: 'Token required' }]
                    : []
                }
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'plus_lead' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help="Include (barcode) as a placeholder, e.g. https://api/x?id=(barcode)"
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'expo_tools' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help="Include (scanCode) as a placeholder"
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'xpress_lead_pro' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help={
              'Paste the full XpressLeadPro filled_url including authkey, ' +
              'exid, eventcode, and placeholder tokens. Required tokens: ' +
              '(badgeNumber), (lastName), (utc_time), (current_time). ' +
              'Also supported: (firstName), (company), (showCode), (barcode).'
            }
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'maritz_swap' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help={
              'Maritz SWAP LeadInfo URL with apikey, actcode, and ' +
              'operator badgeid baked in, plus (barcode) as a ' +
              'placeholder, e.g. https://developer.swap.mge360.com/' +
              'APIv1/LeadInfo?apikey=X&actcode=Y&badgeid=Z&barcode=(barcode)'
            }
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'edc' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help={
              'EDC (retrievemyleads.com) LeadInfo URL with sid (show ' +
              'code) and key baked in, plus (aid) as a placeholder, ' +
              'e.g. https://retrievemyleads.com/api/meta/1.1/read/' +
              '?sid=SHOWCODE&key=KEY&aid=(aid)'
            }
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'badge_kit' && (
          <>
            <Form.Item
              label="Login URL"
              name={['config', 'login_url']}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Data extract URL"
              name={['config', 'data_extract_url']}
              rules={[{ required: true, type: 'url' }]}
              help="Include (referenceId), (eventId), (exhibitorId) placeholders"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Event ID"
              name={['config', 'eventId']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Exhibitor ID"
              name={['config', 'exhibitorId']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="API key"
                name={['secrets', 'apikey']}
                rules={
                  !hasSecret
                    ? [{ required: true, message: 'API key required' }]
                    : []
                }
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
              <Form.Item
                label="Secret"
                name={['secrets', 'secret']}
                rules={
                  !hasSecret
                    ? [{ required: true, message: 'Secret required' }]
                    : []
                }
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'exposmart' && (
          <>
            <Form.Item
              label="URL"
              name={['config', 'url']}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input placeholder="https://api.rcsreg.com/w/exposmart/at/v1.json" />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="API Key"
                name={['secrets', 'api_key']}
                rules={
                  !hasSecret
                    ? [{ required: true, message: 'API Key required' }]
                    : []
                }
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'ats' && (
          <>
            <Form.Item
              label="Field layout"
              name={['config', 'field_layout']}
              rules={[{ required: true, message: 'Field layout required' }]}
              extra={ATS_LAYOUT_EXTRA}
            >
              <Input placeholder={ATS_LAYOUT_PLACEHOLDER} />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="Password"
                name={['secrets', 'password']}
                extra={ATS_PASSWORD_EXTRA}
                rules={[
                  ...(!hasSecret ? [{ required: true, message: 'Password required' }] : []),
                  ATS_PASSWORD_WHITESPACE_RULE,
                ]}
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'fpleads' && (
          <>
            <Form.Item
              label="Base URL"
              name={['config', 'base_url']}
              validateFirst
              rules={[{ required: true, type: 'url' }, FPLEADS_HTTPS_RULE]}
              extra={FPLEADS_BASE_URL_EXTRA}
            >
              <Input />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="API Key"
                name={['secrets', 'api_key']}
                extra={FPLEADS_SECRETS_EXTRA}
                dependencies={[['secrets', 'user_id']]}
                rules={[
                  ({ getFieldValue }) => ({
                    required: !hasSecret || Boolean(getFieldValue(['secrets', 'user_id'])),
                    message: 'API Key required',
                  }),
                  FPLEADS_WHITESPACE_RULE,
                ]}
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
              <Form.Item
                label="User ID"
                name={['secrets', 'user_id']}
                dependencies={[['secrets', 'api_key']]}
                rules={[
                  ({ getFieldValue }) => ({
                    required: !hasSecret || Boolean(getFieldValue(['secrets', 'api_key'])),
                    message: 'User ID required',
                  }),
                  FPLEADS_WHITESPACE_RULE,
                ]}
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'showcare' && (
          <>
            <Form.Item
              label="Base URL"
              name={['config', 'base_url']}
              validateFirst
              rules={[{ required: true, type: 'url' }, SHOWCARE_HTTPS_RULE, SHOWCARE_BASE_URL_SHAPE_RULE]}
              extra={SHOWCARE_BASE_URL_EXTRA}
            >
              <Input />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="License key"
                name={['secrets', 'license_key']}
                extra={SHOWCARE_LICENSE_KEY_EXTRA}
                rules={[
                  ...(!hasSecret ? [{ required: true, message: 'License key required' }] : []),
                  SHOWCARE_LICENSE_KEY_RULE,
                ]}
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'ats_api' && (
          <>
            <Form.Item
              label="Show ID"
              name={['config', 'show_id']}
              rules={[{ required: true, message: 'Show ID required' }, ATS_API_SHOW_ID_RULE]}
              extra={ATS_API_SHOW_ID_EXTRA}
            >
              <Input inputMode="numeric" />
            </Form.Item>
            <Can I="manage" a="Toolkit">
              <Form.Item
                label="Username"
                name={['secrets', 'username']}
                extra={ATS_API_SECRETS_EXTRA}
                dependencies={[['secrets', 'password']]}
                rules={[
                  ({ getFieldValue }) => ({
                    required: !hasSecret || Boolean(getFieldValue(['secrets', 'password'])),
                    message: 'Username required',
                  }),
                  ATS_API_USERNAME_RULE,
                ]}
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
              <Form.Item
                label="Password"
                name={['secrets', 'password']}
                dependencies={[['secrets', 'username']]}
                rules={[
                  ({ getFieldValue }) => ({
                    required: !hasSecret || Boolean(getFieldValue(['secrets', 'username'])),
                    message: 'Password required',
                  }),
                  ATS_API_PASSWORD_RULE,
                ]}
              >
                <MaskedSecretField hasSecret={hasSecret} />
              </Form.Item>
            </Can>
          </>
        )}

        {toolkitType === 'conexsys' && (
          <Alert
            type="warning"
            showIcon
            message="Conexsys integration not implemented yet — saving config is allowed, but the badge-scan endpoint returns 501 until the backend handler ships."
            style={{ marginBottom: space.lg }}
          />
        )}

        {hasSecret && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: space.lg }}
            message="A secret is already stored. Leave secret fields blank to keep it. Type a new value to overwrite."
          />
        )}

        <Form.Item>
          {/*
            No htmlType="submit" here: saveButtonProps.onClick already calls
            form.submit() once. antd's Button defaults to htmlType="button",
            so a click drives exactly that one submit path — a submit button
            inside this <Form> would ALSO trigger the browser's native form
            submit, invoking rc-field-form's own onSubmit -> form.submit()
            a second time.
          */}
          <Button type="primary" {...saveButtonProps}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </RequireCan>
  );
}
